import { Model } from '@vuex-orm/core';
import DeliveryItemAPI from '@/api/DeliveryItemAPI';
import { DeliveryItemCreatePayload, DeliveryItemUpdatePayload } from '@/interfaces/logistics/LogisticsInterfaces';

/**
 * DeliveryItem model for logistics shipment items
 * Represents individual items within a delivery list
 */
export default class DeliveryItem extends Model {
    // Public field declarations for direct access
    public id!: string;
    public delivery_id!: string;
    public sourceOrderNumber!: string;
    public clientName!: string;
    public city!: string;
    public street!: string;
    public pieces!: number;
    public position!: number;
    public contactPhone!: string | null;
    public notes!: string | null;
    public payment!: string | null;
    public checkMaterial!: string | null;
    public plannedDelivery!: string | null;
    public actualDeparture!: string | null;
    public actualArrival!: string | null;
    public createdAt!: string;
    public updatedAt!: string;

    public static entity = 'delivery_items';

    public static fields() {
        return {
            id: this.attr(null),
            delivery_id: this.attr(null),
            sourceOrderNumber: this.attr(null),
            clientName: this.attr(null),
            city: this.attr(null),
            street: this.attr(null),
            pieces: this.number(0),
            position: this.number(0),
            contactPhone: this.attr(null),
            notes: this.attr(null),
            payment: this.attr(null),
            checkMaterial: this.attr(null),
            plannedDelivery: this.attr(null),
            actualDeparture: this.attr(null),
            actualArrival: this.attr(null),
            createdAt: this.attr(null),
            updatedAt: this.attr(null),
        };
    }

    /**
     * Fetch all items for a specific delivery
     */
    public static async fetchByDeliveryId(deliveryId: string): Promise<DeliveryItem[]> {
        try {
            const response = await DeliveryItemAPI.getByDeliveryId(deliveryId);
            const items = response?.data || [];
            await DeliveryItem.insertOrUpdate({ data: items });
            return items;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Create a new delivery item
     */
    public static async createNew(deliveryId: string, itemData: DeliveryItemCreatePayload): Promise<DeliveryItem> {
        try {
            const response = await DeliveryItemAPI.create(deliveryId, itemData);
            const item = response?.data;
            if (item) {
                await DeliveryItem.insertOrUpdate({ data: item });
            }
            return item;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Update an existing delivery item
     */
    public static async updateExisting(id: string, itemData: DeliveryItemUpdatePayload): Promise<DeliveryItem> {
        try {
            const response = await DeliveryItemAPI.update(id, itemData);
            const item = response?.data;
            if (item) {
                await DeliveryItem.insertOrUpdate({ data: item });
            }
            return item;
        } catch (e) {
            return Promise.reject(e);
        }
    }

    /**
     * Delete a delivery item
     */
    public static async deleteExisting(id: string): Promise<void> {
        try {
            await DeliveryItemAPI.delete(id);
            await DeliveryItem.delete(id);
        } catch (e) {
            return Promise.reject(e);
        }
    }
}
